import React, { useState, useEffect } from 'react';
import { useAuth } from './Authcontext';
import { toast, ToastContainer } from 'react-toastify';
import logo from '../images/sadhimalogo-desktop.png';

const API_URL = 'https://api.rangpursadhima.cloud/api';

const UserProfile = () => {
  const { user, logout, token } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editData, setEditData] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [userMudat, setUserMudat] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'

  useEffect(() => {
    if (user?.id) {
      fetchUserData();
      fetchAttendance();
      fetchUserMudat();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      const res = await fetch(`${API_URL}/users/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setUserData(data);
        setEditData(data);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const res = await fetch(`${API_URL}/users/attendance/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setAttendance(data);
      }
    } catch (err) {
      console.error('Error fetching attendance:', err);
    }
  };

  const fetchUserMudat = async () => {
    try {
      const res = await fetch(`${API_URL}/mudat/user-mudat/${user.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setUserMudat(data);
      }
    } catch (err) {
      // No mudat data yet - that's okay
      console.log('No mudat data for user');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      let imageUrl = editData.image_url;

      // Upload image first if selected
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        
        const uploadRes = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          imageUrl = uploadData.imageUrl;
        }
      }

      // Update user data with actor info for activity logging
      const res = await fetch(`${API_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          FIRST_NAME: editData.FIRST_NAME,
          LAST_NAME: editData.LAST_NAME,
          MOBILE_NO: editData.MOBILE_NO,
          CONTACT_NO: editData.CONTACT_NO,
          ADDRESS: editData.ADDRESS,
          CITY_AREA_VILLAGE: editData.CITY_AREA_VILLAGE,
          TALUKA: editData.TALUKA,
          DISTRICT: editData.DISTRICT,
          STATE: editData.STATE,
          PIN_CODE: editData.PIN_CODE,
          DOB: editData.DOB ? editData.DOB.split("T")[0] : null,
          image_url: imageUrl,
          // Actor info for activity logging
          actorId: user.id,
          actorRole: 'USER',
          actorMobile: user.mobile,
          isSelfEdit: true,
        }),
      });

      if (res.ok) {
        toast.success('Profile updated successfully');
        setIsEditing(false);
        setImageFile(null);
        setImagePreview(null);
        fetchUserData();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (err) {
      toast.error('Error updating profile');
      console.error(err);
    }
  };

  const getImageUrl = () => {
    if (imagePreview) return imagePreview;
    if (userData?.image_url) {
      return userData.image_url.startsWith('/upload') 
        ? `https://api.rangpursadhima.cloud${userData.image_url}`
        : userData.image_url;
    }
    return null;
  };

  // Calculate days remaining or late
  const getMudatStatus = () => {
    if (!userMudat?.next_mudat_date) return null;
    
    const nextDate = new Date(userMudat.next_mudat_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextDate.setHours(0, 0, 0, 0);
    
    const diffTime = nextDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { isLate: true, days: Math.abs(diffDays) };
    } else if (diffDays === 0) {
      return { isToday: true, days: 0 };
    } else {
      return { isUpcoming: true, days: diffDays };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const mudatStatus = getMudatStatus();

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-[#F5F5DC] shadow">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <img src={logo} alt="Logo" className="h-10" />
            <div className="flex items-center space-x-4">
              <span className="px-2 py-1 text-xs font-semibold text-white bg-gray-600 rounded">
                USER
              </span>
              <button
                onClick={logout}
                className="rounded-md bg-green-900 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          {/* Profile Card */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-red-600 px-6 py-4">
              <h1 className="text-2xl font-bold text-white">My Profile</h1>
            </div>

            <div className="p-6">
              {/* User Image */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-red-600">
                    {getImageUrl() ? (
                      <img
                        src={getImageUrl()}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-300 flex items-center justify-center text-4xl text-gray-600">
                        {userData?.FIRST_NAME?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <label className="absolute bottom-0 right-0 bg-red-600 text-white p-2 rounded-full cursor-pointer hover:bg-red-700">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      📷
                    </label>
                  )}
                </div>
              </div>

              {/* Sevak Code Badge */}
              <div className="text-center mb-6">
                <span className="px-4 py-2 bg-red-100 text-red-700 font-bold rounded-full">
                  {userData?.Sevakcode}
                </span>
              </div>
                
              <div className="grid md:grid-cols-2 gap-4">
                {isEditing ? (
                  <>
                    {/* Editable Fields */}
                    <EditField
                      label="First Name"
                      value={editData.FIRST_NAME}
                      onChange={(val) => setEditData({ ...editData, FIRST_NAME: val })}
                    />
                    <EditField
                      label="Last Name"
                      value={editData.LAST_NAME}
                      onChange={(val) => setEditData({ ...editData, LAST_NAME: val })}
                    />
                    <EditField
                      label="Mobile"
                      value={editData.MOBILE_NO}
                      onChange={(val) => setEditData({ ...editData, MOBILE_NO: val })}
                    />
                    <EditField
                      label="Contact No"
                      value={editData.CONTACT_NO}
                      onChange={(val) => setEditData({ ...editData, CONTACT_NO: val })}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">DOB</label>
                      <input
                        type="date"
                        value={editData.DOB ? editData.DOB.split("T")[0] : ''}
                        onChange={(e) => setEditData({ ...editData, DOB: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <EditField
                      label="State"
                      value={editData.STATE}
                      onChange={(val) => setEditData({ ...editData, STATE: val })}
                    />
                    <EditField
                      label="District"
                      value={editData.DISTRICT}
                      onChange={(val) => setEditData({ ...editData, DISTRICT: val })}
                    />
                    <EditField
                      label="Taluka"
                      value={editData.TALUKA}
                      onChange={(val) => setEditData({ ...editData, TALUKA: val })}
                    />
                    <EditField
                      label="City/Area/Village"
                      value={editData.CITY_AREA_VILLAGE}
                      onChange={(val) => setEditData({ ...editData, CITY_AREA_VILLAGE: val })}
                    />
                    <EditField
                      label="PIN Code"
                      value={editData.PIN_CODE}
                      onChange={(val) => setEditData({ ...editData, PIN_CODE: val })}
                    />
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                      <textarea
                        value={editData.ADDRESS || ''}
                        onChange={(e) => setEditData({ ...editData, ADDRESS: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        rows={2}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* View Mode */}
                    <InfoField label="Name" value={`${userData?.FIRST_NAME || ''} ${userData?.LAST_NAME || ''}`} />
                    <InfoField label="Mobile" value={userData?.MOBILE_NO} />
                    <InfoField label="Contact No" value={userData?.CONTACT_NO} />
                    <InfoField label="DOB" value={userData?.DOB} />
                    <InfoField label="State" value={userData?.STATE} />
                    <InfoField label="District" value={userData?.DISTRICT} />
                    <InfoField label="Taluka" value={userData?.TALUKA} />
                    <InfoField label="City/Area/Village" value={userData?.CITY_AREA_VILLAGE} />
                    <InfoField label="PIN Code" value={userData?.PIN_CODE} />
                    <InfoField label="Address" value={userData?.ADDRESS} colSpan={2} />
                  </>
                )}
              </div>

              {/* Edit/Save Buttons */}
              <div className="mt-6 flex justify-end space-x-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditData(userData);
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mudat Section with Tabs */}
          <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-red-600 px-6 py-4">
              <h2 className="text-xl font-bold text-white">મારી મુદત</h2>
            </div>
            
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('upcoming')}
                  className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                    activeTab === 'upcoming'
                      ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  🗓️ આગામી મુદત
                </button>
                <button
                  onClick={() => setActiveTab('past')}
                  className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                    activeTab === 'past'
                      ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  📜 જૂની હાજરી
                </button>
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'upcoming' ? (
                /* Upcoming Mudat Tab */
                <div>
                  {userMudat ? (
                    <div className="space-y-6">
                      {/* Next Mudat Card */}
                      <div className={`p-6 rounded-lg border-2 ${
                        mudatStatus?.isLate 
                          ? 'bg-red-50 border-red-300' 
                          : mudatStatus?.isToday 
                          ? 'bg-yellow-50 border-yellow-300'
                          : 'bg-green-50 border-green-300'
                      }`}>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-2">આગામી મુદત તારીખ</p>
                          <p className={`text-3xl font-bold ${
                            mudatStatus?.isLate 
                              ? 'text-red-700' 
                              : mudatStatus?.isToday 
                              ? 'text-yellow-700'
                              : 'text-green-700'
                          }`}>
                            {new Date(userMudat.next_mudat_date).toLocaleDateString('gu-IN', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          
                          {/* Status Badge */}
                          <div className="mt-4">
                            {mudatStatus?.isLate && (
                              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                                ⚠️ {mudatStatus.days} દિવસ મોડું થયું
                              </span>
                            )}
                            {mudatStatus?.isToday && (
                              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                                🔔 આજે મુદત છે!
                              </span>
                            )}
                            {mudatStatus?.isUpcoming && (
                              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                                ✓ {mudatStatus.days} દિવસ બાકી
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Mudat Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <p className="text-sm text-gray-500">મુદત દિવસો</p>
                          <p className="text-xl font-bold text-gray-800">{userMudat.mudat_days} દિવસ</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <p className="text-sm text-gray-500">જિલ્લો</p>
                          <p className="text-xl font-bold text-gray-800">{userMudat.district || userData?.DISTRICT || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <p className="text-sm text-gray-500">છેલ્લી હાજરી</p>
                          <p className="text-lg font-bold text-gray-800">
                            {userMudat.last_marked 
                              ? new Date(userMudat.last_marked).toLocaleDateString('gu-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })
                              : '-'
                            }
                          </p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-center">
                          <p className="text-sm text-gray-500">કુલ હાજરી</p>
                          <p className="text-xl font-bold text-gray-800">{attendance.length}</p>
                        </div>
                      </div>

                      {/* Reminder Note */}
                      {mudatStatus?.isLate && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                          <p className="text-red-700">
                            <strong>⚠️ ધ્યાન આપો:</strong> તમારી મુદત મોડી થઈ ગઈ છે. કૃપા કરીને જલ્દીથી મંદિરે આવો.
                          </p>
                        </div>
                      )}
                      {mudatStatus?.isToday && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                          <p className="text-yellow-700">
                            <strong>🔔 યાદ રાખો:</strong> આજે તમારી મુદત છે!
                          </p>
                        </div>
                      )}
                      {mudatStatus?.isUpcoming && mudatStatus.days <= 7 && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                          <p className="text-blue-700">
                            <strong>📅 રીમાઇન્ડર:</strong> તમારી મુદત {mudatStatus.days} દિવસમાં છે.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-6xl mb-4">📅</div>
                      <p className="text-gray-500 text-lg">હજુ સુધી કોઈ મુદત નક્કી નથી</p>
                      <p className="text-gray-400 text-sm mt-2">તમારી પ્રથમ હાજરી પછી મુદત સેટ થશે</p>
                    </div>
                  )}
                </div>
              ) : (
                /* Past Attendance Tab */
                <div>
                  {attendance.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">સ્લોટ</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">સમય</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">તારીખ</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {attendance.map((record, index) => {
                            const dateObj = new Date(record.timestamp);
                            const isMorning = record.slot?.toLowerCase().includes('morning');
                            const isEvening = record.slot?.toLowerCase().includes('evening');
                            
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                    isMorning 
                                      ? 'bg-yellow-100 text-yellow-800' 
                                      : isEvening 
                                      ? 'bg-indigo-100 text-indigo-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {isMorning ? '🌅 સવાર' : isEvening ? '🌙 સાંજ' : record.slot}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {dateObj.toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {dateObj.toLocaleDateString('gu-IN', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-6xl mb-4">📋</div>
                      <p className="text-gray-500 text-lg">હજુ સુધી કોઈ હાજરી નોંધાયેલ નથી</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

// Helper component for displaying info fields (view mode)
const InfoField = ({ label, value, colSpan = 1 }) => (
  <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-gray-500">{label}</label>
    <p className="mt-1 text-gray-900">{value || '-'}</p>
  </div>
);

// Helper component for editable fields
const EditField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border rounded-md"
    />
  </div>
);

export default UserProfile;